import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatNumber, formatToman } from "../utils/format";

export interface DashboardBucket {
  time: string;
  end: string;
  values: Record<string, number>;
}

export interface DashboardUsageData {
  models: string[];
  buckets: DashboardBucket[];
  totals: Record<string, number>;
  summary: { tokens: number; spend: number; requests: number };
}

interface StackedAreaChartProps {
  data?: DashboardUsageData;
  duration: string;
  metric: string;
  loading?: boolean;
  showFullDateLabels?: boolean;
}

const OTHERS = "others";
const OTHERS_LABEL = "سایر";
const PALETTE = ["#22C55E", "#60A5FA", "#A78BFA", "#FB923C"];
const OTHERS_COLOR = "#94A3B8";
const HEIGHT = 280;
const PAD = { top: 16, right: 12, bottom: 30, left: 12 };
// Mobile-only gutter: long Persian tick labels (tokens/costs) overflow the
// chart border with the 12px desktop gutter, so widen it on small screens.
const MOBILE_LEFT_GUTTER = 54;
const LEGEND_HEIGHT = 36;

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const frac = max / pow;
  let nice = 10;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  return nice * pow;
}

function formatXLabel(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y") return d.toLocaleString("fa-IR", { month: "short" });
  if (duration === "all")
    // Default fallback for "all" when span is unknown — show day + month
    return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (duration === "today" || duration === "1h")
    return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
  if (duration === "30d") return d.toLocaleString("fa-IR", { day: "numeric" });
  return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
}

function formatRange(start: string, end: string, duration: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (duration === "today" || duration === "1h")
    return `${s.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })} — ${e.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })}`;
  if (duration === "1y")
    return s.toLocaleString("fa-IR", { month: "long", year: "numeric" });
  if (duration === "all")
    return `${s.toLocaleString("fa-IR", { day: "numeric", month: "short", year: "numeric" })} — ${e.toLocaleString("fa-IR", { day: "numeric", month: "short", year: "numeric" })}`;
  return `${s.toLocaleString("fa-IR", { day: "numeric", month: "long" })} — ${e.toLocaleString("fa-IR", { day: "numeric", month: "long" })}`;
}

function formatValue(metric: string, value: number): string {
  if (metric === "spend") return formatToman(value);
  return formatNumber(value);
}

function metricUnit(metric: string): string {
  if (metric === "spend") return "تومان";
  if (metric === "requests") return "درخواست";
  return "توکن";
}

function useIsMobile(breakpoint = 767): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(max-width: ${breakpoint}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);
  return isMobile;
}

interface Pt {
  x: number;
  y: number;
}

// Smooths a polyline through the given points using Catmull-Rom -> cubic
// Bezier conversion. Control points are clamped to keep the curve inside the
// plot area, so no overshoot below the baseline. No extra data points are
// added — only the curve between existing points becomes smooth.
function smoothSegments(pts: Pt[], minY: number, maxY: number): string {
  if (pts.length === 0) return "";
  let d = "";
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    let c1x = p1.x + (p2.x - p0.x) / 6;
    let c1y = p1.y + (p2.y - p0.y) / 6;
    let c2x = p2.x - (p3.x - p1.x) / 6;
    let c2y = p2.y - (p3.y - p1.y) / 6;
    c1y = Math.min(maxY, Math.max(minY, c1y));
    c2y = Math.min(maxY, Math.max(minY, c2y));
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

export default function StackedAreaChart({ data, duration, metric, loading, showFullDateLabels = false }: StackedAreaChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hoveredBucket, setHoveredBucket] = useState<number | null>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);
  const [tooltipW, setTooltipW] = useState(0);
  const [tooltipH, setTooltipH] = useState(0);
  const [isSwitching, setIsSwitching] = useState(false);
  const flowKey = `${duration}-${metric}`;
  const flowKeyRef = useRef(flowKey);
  const pendingRef = useRef<string | null>(null);
  const prevDataRef = useRef(data);
  const uid = useId().replace(/[:]/g, "");
  const isMobile = useIsMobile();
  // Mobile only: wider left gutter so long y-axis labels stay inside the chart.
  const padL = isMobile ? MOBILE_LEFT_GUTTER : PAD.left;

  // Flow transition: keep `isSwitching` true from tab change until new data arrives,
  // so first-time selections (uncached) also animate instead of flashing.
  useLayoutEffect(() => {
    if (flowKey === flowKeyRef.current) return;
    flowKeyRef.current = flowKey;
    pendingRef.current = flowKey;
    setIsSwitching(true);
  }, [flowKey]);

  useEffect(() => {
    if (!pendingRef.current) {
      prevDataRef.current = data;
      return;
    }
    // still waiting for new data for the pending key?
    if (data === prevDataRef.current) return;
    // Ignore placeholder/loading states – wait for real buckets
    if (loading) return;
    const hasReal = !!(data && (data as DashboardUsageData).buckets?.length);
    if (!hasReal) return;
    // new data arrived for pending key
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        setIsSwitching(false);
        pendingRef.current = null;
        prevDataRef.current = data;
        flowKeyRef.current = flowKey;
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [data, flowKey, loading]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, data]);

  const buckets = data?.buckets || [];
  // For "all" duration, adapt X-axis density and labels to the actual account age
  const allSpanDays = useMemo(() => {
    if (duration !== "all" || buckets.length === 0) return 0;
    const first = new Date(buckets[0].time).getTime();
    const last = new Date(buckets[buckets.length - 1].end).getTime();
    if (isNaN(first) || isNaN(last)) return 0;
    return (last - first) / 86400000;
  }, [duration, buckets]);

  const getAllXLabel = (iso: string) => {
    const d = new Date(iso);
    if (allSpanDays > 180) return d.toLocaleString("fa-IR", { month: "short", year: "2-digit" });
    if (allSpanDays > 30) return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
    return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  };
  const getAllRange = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    if (allSpanDays > 180) return s.toLocaleString("fa-IR", { month: "long", year: "numeric" });
    return `${s.toLocaleString("fa-IR", { day: "numeric", month: "short", year: "numeric" })} — ${e.toLocaleString("fa-IR", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  const baseSeries = useMemo(() => [...(data?.models || [])], [data]);
  const colorFor = useMemo(() => {
    const map = new Map<string, string>();
    baseSeries.forEach((s, i) => {
      if (s === OTHERS) map.set(s, OTHERS_COLOR);
      else map.set(s, PALETTE[i % PALETTE.length]);
    });
    return map;
  }, [baseSeries]);
  const visibleSeries = baseSeries.filter((s) => !hidden.has(s));

  const toggleSeries = (name: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const { stacked, maxY, grandTotal } = useMemo(() => {
    const stacked = buckets.map((b) => {
      const cum: Record<string, number> = {};
      let acc = 0;
      for (const s of visibleSeries) {
        acc += Number(b.values[s]) || 0;
        cum[s] = acc;
      }
      return { total: acc, cum };
    });
    const maxY = niceMax(Math.max(0, ...stacked.map((s) => s.total)));
    const grandTotal = stacked.reduce((sum, s) => sum + s.total, 0);
    return { stacked, maxY, grandTotal };
  }, [buckets, visibleSeries]);

  const n = buckets.length;
  const innerW = Math.max(1, width - padL - PAD.right);
  const innerH = HEIGHT - PAD.top - PAD.bottom - LEGEND_HEIGHT;
  const x = (i: number) => (n <= 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (maxY > 0 ? v / maxY : 0) * innerH;

  const ticks = [0, 1, 2, 3, 4].map((i) => (maxY * i) / 4);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const ratio = (relX - padL) / innerW;
    const index = Math.min(Math.max(Math.round(ratio * (n - 1)), 0), n - 1);
    setHoveredBucket(index);
    setMouseX(relX);
    setMouseY(relY);
  };

  const isEmpty = !n || grandTotal <= 0;

  const hoveredEntries =
    hoveredBucket != null && !isEmpty
      ? visibleSeries
          .map((s) => ({
            name: s,
            color: colorFor.get(s) || "#999",
            value: Number(buckets[hoveredBucket].values[s]) || 0,
          }))
          .filter((e) => e.value > 0)
      : [];
  const hoveredTotal =
    hoveredBucket != null && !isEmpty
      ? visibleSeries.reduce((sum, s) => sum + (Number(buckets[hoveredBucket].values[s]) || 0), 0)
      : 0;

  const polygons = visibleSeries.map((s, k) => {
    const topPts = buckets.map((_, i) => ({ x: x(i), y: y(stacked[i].cum[s] || 0) }));
    const bottomPts =
      k === 0
        ? buckets.map((_, i) => ({ x: x(i), y: y(0) }))
        : buckets.map((_, i) => ({ x: x(i), y: y(stacked[i].cum[visibleSeries[k - 1]] || 0) }));
    const baseline = PAD.top + innerH;
    const topStart = topPts[0];
    const bottomStart = bottomPts[bottomPts.length - 1];
    const path = `M ${topStart.x.toFixed(1)} ${topStart.y.toFixed(1)}${smoothSegments(
      topPts,
      PAD.top,
      baseline
    )} L ${bottomStart.x.toFixed(1)} ${bottomStart.y.toFixed(1)}${smoothSegments(
      [...bottomPts].reverse(),
      PAD.top,
      baseline
    )} Z`;
    return { name: s, path, color: colorFor.get(s) || "#999" };
  });

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipW(tooltipRef.current.offsetWidth);
      setTooltipH(tooltipRef.current.offsetHeight);
    }
  }, [hoveredBucket, hoveredEntries.length]);

  const GAP = 10;
  const TOOLTIP_EDGE = 6;
  // Vertical: center the tooltip on the mouse, clamped to the plot area.
  const rawTop = mouseY != null ? mouseY - tooltipH / 2 : PAD.top + 2;
  const tooltipTop = Math.min(Math.max(rawTop, PAD.top + 2), HEIGHT - tooltipH - TOOLTIP_EDGE);
  // Horizontal: place the tooltip on whichever side of the mouse has more room.
  let tooltipLeft = padL;
  if (mouseX != null) {
    const spaceLeft = mouseX - GAP;
    const spaceRight = width - mouseX - GAP;
    if (spaceRight >= spaceLeft) {
      tooltipLeft = mouseX + GAP;
    } else {
      tooltipLeft = mouseX - GAP - tooltipW;
    }
    tooltipLeft = Math.min(Math.max(tooltipLeft, TOOLTIP_EDGE), width - tooltipW - TOOLTIP_EDGE);
  }
  const tooltipLeftPct = `${tooltipLeft}px`;

  const chartSwitchClass = `chart-transition ${isSwitching ? "chart-switching" : "chart-idle"}`;

  return (
    <div>
      <div
        ref={containerRef}
        dir="ltr"
        className="relative select-none"
        style={{ height: HEIGHT }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredBucket(null)}
      >
        {loading ? (
          <div className="h-full w-full flex items-center justify-center">
            <div className="h-44 w-full bg-surface-hover rounded animate-pulse" />
          </div>
        ) : isEmpty ? (
          <div className="h-full w-full flex items-center justify-center text-text-tertiary">
            <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <div className={chartSwitchClass}>
            {/* Y axis ticks */}
            <div className="absolute left-0 top-0 bottom-0" style={{ width: padL }}>
              {ticks.map((t) => (
                <span
                  key={t}
                  dir="rtl"
                  className="stacked-y-tick absolute right-0 -translate-y-1/2 text-[10px] text-text-tertiary"
                  style={{ top: `${(y(t) / HEIGHT) * 100}%` }}
                >
                  {formatNumber(t)}
                </span>
              ))}
            </div>

            {width > 0 && (
              <svg width={width} height={HEIGHT} className="block overflow-visible">
                <defs>
                  {polygons.map((p, k) => (
                    <linearGradient
                      key={p.name}
                      id={`${uid}-${k}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={p.color} stopOpacity="0.95" />
                      <stop offset="100%" stopColor={p.color} stopOpacity="0" />
                    </linearGradient>
                  ))}
                </defs>

                {/* Horizontal gridlines */}
                {ticks.map((t) => (
                  <line
                    key={t}
                    x1={padL}
                    x2={width - PAD.right}
                    y1={y(t)}
                    y2={y(t)}
                    stroke="rgb(var(--color-border))"
                    strokeWidth="1"
                  />
                ))}

                {/* Stacked area polygons - staggered fade for flow */}
                {polygons.map((p, k) => (
                  <path
                    key={p.name}
                    d={p.path}
                    fill={`url(#${uid}-${k})`}
                    stroke={p.color}
                    strokeWidth="1"
                    strokeLinejoin="round"
                    style={{
                      opacity: isSwitching ? 0 : 1,
                      transition: `opacity 420ms ease ${k * 70}ms, d 520ms cubic-bezier(0.22,1,0.36,1)`,
                    }}
                  />
                ))}

                {/* Hover guide */}
                {hoveredBucket != null && (
                  <line
                    x1={x(hoveredBucket)}
                    x2={x(hoveredBucket)}
                    y1={PAD.top}
                    y2={PAD.top + innerH}
                    stroke="rgb(var(--text-main))"
                    strokeWidth="1"
                    strokeDasharray="3 3"
                    opacity="0.35"
                  />
                )}
              </svg>
            )}

            {/* Tooltip */}
            {hoveredBucket != null && hoveredEntries.length > 0 && (
              <div
                dir="rtl"
                className="absolute z-30 pointer-events-none"
                style={{ left: tooltipLeftPct, top: tooltipTop }}
              >
                <div
                  ref={tooltipRef}
                  className="px-3 py-2.5 rounded-btn whitespace-nowrap"
                  style={{
                    background: "rgba(26,26,26,0.92)",
                    color: "#fff",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  }}
                >
                  <p className="text-[11px] mb-1.5" style={{ opacity: 0.8 }}>
                    {duration === "all"
                      ? getAllRange(buckets[hoveredBucket].time, buckets[hoveredBucket].end)
                      : formatRange(buckets[hoveredBucket].time, buckets[hoveredBucket].end, duration)}
                  </p>
                  {hoveredEntries.map((e) => (
                    <div key={e.name} className="flex items-center justify-between gap-5 py-0.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color }} />
                        <span dir="ltr" className="text-[11px] truncate max-w-[170px]">
                          {e.name === OTHERS ? OTHERS_LABEL : e.name}
                        </span>
                      </div>
                      <span className="text-xs font-medium" style={{ color: "rgb(var(--chart-green))" }}>
                        {formatValue(metric, e.value)} {metricUnit(metric)}
                      </span>
                    </div>
                  ))}
                  <div className="my-1 h-px" style={{ background: "rgba(255,255,255,0.15)" }} />
                  <div className="flex items-center justify-between gap-5">
                    <span className="text-[11px]" style={{ opacity: 0.8 }}>مجموع</span>
                    <span className="text-xs font-bold" style={{ color: "rgb(var(--chart-green))" }}>
                      {formatValue(metric, hoveredTotal)} {metricUnit(metric)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!loading && !isEmpty && (
        <div className={chartSwitchClass} style={{ transitionDelay: isSwitching ? "60ms" : "0ms" }}>
          {/* X axis labels */}
          <div className="flex gap-1 mt-1" dir="ltr">
            {buckets.map((b, i) => (
              <div
                key={i}
                dir="rtl"
                className="flex-1 text-center text-[10px] text-text-tertiary truncate"
                style={{ transition: `opacity 320ms ease ${i * 18}ms`, opacity: isSwitching ? 0 : 1 }}
              >
                {showFullDateLabels
                  ? new Date(b.time).toLocaleString("fa-IR", { day: "numeric", month: "short" })
                  : duration === "all" ? getAllXLabel(b.time) : formatXLabel(b.time, duration)}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-4">
            {baseSeries.map((s, idx) => {
              const isHidden = hidden.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSeries(s)}
                  className={`flex items-center gap-2 text-body text-text-main transition-all ${
                    isHidden ? "line-through opacity-40" : "font-medium"
                  }`}
                  style={{ transitionDelay: `${idx * 40}ms`, opacity: isSwitching ? 0 : 1, transform: isSwitching ? "translateY(4px)" : "translateY(0)" }}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorFor.get(s) }} />
                  <span dir="ltr">{s === OTHERS ? OTHERS_LABEL : s}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
